import React, {ReactElement, ReactNode, useEffect, useMemo, useState} from 'react';
import {
  Button,
  Card,
  CardContent,
  Typography,
  makeStyles,
  createStyles,
  Theme,
  Link
} from '@material-ui/core';
import {Trans, useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';

import NavRoutes from 'src/constants/route/NavRoutes';
import {APP, APP_PRIMARY_COLOR} from 'src/style/variable';
import ClinicGroupsIcon from '@components/system/MyIcons/ClinicGroupsIcon';
import Loader from '@components/framework/loader/Loader';

// redux state action method
import './../Home/AppointmentSchedulerHomeStyles.scss';
import HelperUtils from '@shared/utils/HelperUtils';
import PhoneFormatter from '@components/system/PhoneFormatter/PhoneFormatter';
import VaccineCard from '../../components/VaccineCard/VaccineCard';
import {CustomRouteComponentProps} from 'src/constants/route/CustomRoute';
import {
  _appointmentPortalLogin,
  _appointmentPortalLogout,
  _setAppointmentPortalCommonDetails,
  _setAppointmentPortalInsuranceDetails,
  _setAvailableVaccinesList
} from '@shared/store/actions/AppointmentSchedularAction';
import {
  getAllAppointmentsInPortal,
  verifyAppointmentPortalToken,
  getInsuranceDetails,
  getAvailableVaccine
} from '@shared/services/AppointmentSchedulingService';
import {
  GetAppointmentsResponse,
  InsuranceCardDetails,
  Patient
} from '@shared/services/service-mappers/AppointmentSchedulingMapper';
import {State} from '@shared/store/reducers/Index';
import WelcomeScreen from '../WelcomeScreen/WelcomeScreen';
import ScheduleAppointmentChoiceDialog from '../../components/ScheduleAppointmentChoiceDialog/ScheduleAppointmentChoiceDialog';
import AppointmentPortalPublicPageLayout from '../../components/AppointmentPortalPublicPageLayout/AppointmentPortalPublicPageLayout';
// import {_guarantorLogin} from '@shared/store/actions/PaymentActions';
import PracticeSelection from 'src/modules/payments/dashboard/components/PracticeSelection';
import {LocationsInterface} from '@shared/services/service-mappers/PaymentServiceMapper';
import {guarantorRefresh} from '@shared/services/Payment.service';
import InitSharedRepo from '../../../../../../shared';
import {_setUpdateScheduledAppointment} from '@shared/store/actions/PaymentActions';
const {innerWidth: width} = window;
const isAppointmentPortalDesktop = width >= 1024;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    loginButton: {
      fontFamily: APP.FONT_FAMILY.BOLD,
      flex: 1,
      padding: '1rem 0.5rem'
    },
    card: {
      borderRadius: 0,
      boxShadow: 'none'
    },
    cardPadding: {
      '& .MuiCardContent-root': {
        padding: 0
      },
      padding: 0
    },
    scrollSection: {
      // overflow: 'auto',
      height: '100%',
      padding: '0 12px 32px 0'
    },
    displayFlex: {
      display: 'flex'
    },

    link: {
      cursor: 'pointer',
      color: APP_PRIMARY_COLOR,
      textDecoration: 'underline',
      fontSize: '1rem',
      lineHeight: '1rem'
    },
    ClickHere: {
      textAlign: 'start'
    },
    schedules: {
      textAlign: 'start',
      fontSize: '1.5rrem',
      fontFamily: 'RobotoMedium',
      paddingTop: '1rem'
    },
    noAppointmentScheduledWrapper: {
      marginTop: '2rem'
    },
    loaderWrapper: {
      display: 'flex',
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center'
    },
    logoutButtonWrapper: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      display: 'flex',
      flex: 1,
      paddingTop: '2rem'
    },
    labelWrapper: {
      marginRight: '0.25rem'
    },
    clickHereLink: {
      fontWeight: 'bold',
      fontSize: '1.3rem'
    },
    linkPadding: {
      paddingLeft: '1rem'
    }
  })
);

const AppointmentSchedulerHome = (
  props: CustomRouteComponentProps<{code: string}>
): ReactElement => {
  const classes = useStyles();
  const {history, match} = props;
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const [changeLocation, setChangeLocation] = useState<boolean>(false);
  const [appointmentList, setAppointmentList] = useState<{
    isLoading: boolean;
    data: GetAppointmentsResponse[];
  }>({
    isLoading: true,
    data: []
  });
  // const [
  //   goToPaymentsButtonEvent,
  //   setGoToPaymentsButtonEvent
  // ] = useState<boolean>(false);

  const apiResponseData = useSelector(
    (state: State) => state.appointmentPortal.details
  );

  const locations: Array<LocationsInterface> = useSelector(
    (state: any) => state.appointmentPortal.details?.locations || []
  );

  const getLoggedInPatient = (
    patients: Patient[] | undefined,
    loggedInPatientId: number
  ): Patient | undefined => {
    let loggedInPatient;
    if (Array.isArray(patients) && loggedInPatientId !== undefined) {
      loggedInPatient = patients.find(
        patient => patient.id === loggedInPatientId
      );
      if (!loggedInPatient) {
        return patients[0];
      }
      return loggedInPatient;
    }
  };

  const loggedInPatient = getLoggedInPatient(
    apiResponseData?.patient as Patient[],
    apiResponseData?.logged_in_patient.id as number
  );

  const isLogin = useSelector(
    (state: State) => state.appointmentPortal.isLogin
  );
  // const guarantorPortalIsLogin = useSelector(
  //   (state: State) => state.payments.isLogin
  // );

  const [isLoadingAppointmentList, setIsLoadingAppointmentList] =
    useState<boolean>(true);

  const [stateObj, setStateObj] = useState({
    isLoading: true,
    showWelcomeScreen: false,
    navigateToUploadInsurance: false
  });
  const [showScheduleChoiceDialog, setShowScheduleChoiceDialog] =
    useState<boolean>(false);

  const token = match.params.code;

  const voiceAssistantShortCode = useMemo(() => {
    const clinicShortCode = apiResponseData?.clinic?.short_code;
    if (clinicShortCode) {
      return clinicShortCode;
    }

    const clinicId = apiResponseData?.clinic?.id;
    if (clinicId && Array.isArray(locations) && locations.length > 0) {
      const matchedLocation = locations.find(location => location.id === clinicId);
      return matchedLocation?.short_code || '';
    }

    return '';
  }, [apiResponseData?.clinic?.id, apiResponseData?.clinic?.short_code, locations]);

  const navigateToAppointmentScheduling = (): void => {
    // Navigate to appointment scheduling
    dispatch(
      _setUpdateScheduledAppointment({
        updateAppointment: false
      })
    );
    history.push(
      `/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.BASE}/${token}/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.SCHEDULE_APPOINTMENT}`
    );
    // setGoToPaymentsButtonEvent(false);
  };

  const openScheduleChoiceDialog = (): void => {
    setShowScheduleChoiceDialog(true);
  };

  const navigateToVoiceAssistant = (): void => {
    if (!voiceAssistantShortCode) {
      return;
    }
    setShowScheduleChoiceDialog(false);
    const path = `/${NavRoutes.PUBLIC.VOICE_ASSISTANT}/${voiceAssistantShortCode}`;
    window.open(`${window.location.origin}${path}`, '_blank', 'noopener,noreferrer');
  };

  const handleSelectNormalScheduling = (): void => {
    setShowScheduleChoiceDialog(false);
    navigateToAppointmentScheduling();
  };

  const logoutAction = async (): Promise<void> => {
    // making async call to logoff guarantor.
    // logoutGuarantor();
    dispatch(_appointmentPortalLogin(false));
    dispatch(_appointmentPortalLogout());

    history.push(
      `/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.BASE}/${token}/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.LOGIN}`
    );
  };

  const navigateToUploadScreen = () => {
    history.push(
      `/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.BASE}/${token}/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.UPLOAD_INSURANCE}`
    );
  };

  useEffect(() => {
    if (!stateObj.showWelcomeScreen && stateObj.navigateToUploadInsurance) {
      navigateToUploadScreen();
      setStateObj(p => ({...p, navigateToUploadInsurance: false}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateObj.navigateToUploadInsurance, stateObj.showWelcomeScreen]);

  const handleSelectPractice = async (clinic: any): Promise<void> => {
    guarantorRefresh(clinic?.id)
      .then(res => {
        if (res.data) {
          InitSharedRepo.initAfterAuth({token: res.data?.token});
          // VERIFY REFRESH TOKEN...
          init(); // eslint-disable-line no-use-before-define
        }
      })
      .catch(err => {
        if (err) {
          logoutAction();
        }
      });
  };

  const init = async () => {
    setStateObj(p => ({...p, isLoading: true}));
    try {
      const verifyRes = await verifyAppointmentPortalToken();
      if (verifyRes.data) {
        dispatch(_setAppointmentPortalCommonDetails(verifyRes.data));
      }
      const isClinicExists = Object.keys(verifyRes?.data?.clinic)?.length > 0;
      if (!isClinicExists) {
        setChangeLocation(true);
      }
      const loggedInPatientDefault = getLoggedInPatient(
        verifyRes.data.patient,
        verifyRes.data.logged_in_patient.id
      );
      const insuranceRes = await getInsuranceDetails();
      let loggedInPatientInsurance: InsuranceCardDetails | undefined;
      if (
        Array.isArray(insuranceRes.data.insurance) &&
        verifyRes.data.logged_in_patient.id
      ) {
        loggedInPatientInsurance = insuranceRes.data.insurance.find(
          ins => ins.patient_id === verifyRes.data.logged_in_patient.id
        );
      }
      // If there is no insurance information, navigate to the upload insurance screen
      // if (!insuranceRes.data) {
      //   history.push(
      //     `/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.BASE}/${token}/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.UPLOAD_INSURANCE}`
      //   );
      // }
      setStateObj(p => ({
        ...p,
        // No insurance details, Need to navigate
        navigateToUploadInsurance: !loggedInPatientInsurance,
        // Show welcome screen if it is a new patient
        showWelcomeScreen:
          !!loggedInPatientDefault?.is_appointment_patient &&
          !loggedInPatientInsurance
      }));
      if (loggedInPatientInsurance) {
        dispatch(
          _setAppointmentPortalInsuranceDetails(loggedInPatientInsurance)
        );
      }
      setAppointmentList(p => ({
        ...p,
        isLoading: true
      }));
      // Fetch appointments list only if clinic id exists in token
      if (isClinicExists) {
        const [vaccinesResponse, allAppointmentResponse] = await Promise.all([
          getAvailableVaccine({
            params: {
              clinic_id: verifyRes.data?.clinic?.id as number
            }
          }),
          getAllAppointmentsInPortal({
            params: {
              patient_id: verifyRes.data.logged_in_patient.id,
              clinic_id: verifyRes.data?.clinic?.id
            }
          })
        ]);
        if (vaccinesResponse.data?.length) {
          dispatch(_setAvailableVaccinesList(vaccinesResponse.data));
        }
        setAppointmentList({
          isLoading: false,
          data: allAppointmentResponse.data.rows
        });
      }
    } catch (err: any) {
      logoutAction();
    } finally {
      setIsLoadingAppointmentList(false);
      setStateObj(p => ({...p, isLoading: false}));
    }
  };

  useEffect(() => {
    if (isLogin) {
      init();
    } else {
      logoutAction();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const renderPatientVaccineCardDetails = (): ReactNode => {
    if (appointmentList.isLoading) {
      return <Loader />;
    }
    if (!appointmentList.data?.length) {
      return (
        <div className={classes.noAppointmentScheduledWrapper}>
          {t(
            'APPOINTMENTS_SCHEDULAR_PORTAL.HOME_SCREEN.NO_APPOINTMENT_SCHEDULED'
          )}
        </div>
      );
    }
    return (
      <div className="vaccine-cards-wrapper">
        {appointmentList.data.map(appointment => {
          return (
            <VaccineCard
              key={appointment.id}
              clinicZone={apiResponseData?.clinic.zone}
              appointmentObj={appointment}
              clinicId={apiResponseData?.clinic?.id as number}
              onAppointmentCancelled={() => {
                void init();
              }}
            />
          );
        })}
      </div>
    );
  };

  const renderEachVaccineCard = () => {
    if (isLoadingAppointmentList) {
      return <Loader />;
    }
    return (
      <>
        {/* cards view start */}
        {renderPatientVaccineCardDetails()}
        {/* cards view end */}

        {/* divider View start */}
        {/* <div className="dividerView" /> */}
        {/* divider view end */}
      </>
    );
  };

  if (stateObj.showWelcomeScreen) {
    return (
      <AppointmentPortalPublicPageLayout
        className="payment-login-page appointment-scheduler-dashboard-page"
        testId="pcl-appointmentPortalWelcomePage">
        <WelcomeScreen
          open={stateObj.showWelcomeScreen}
          onClose={() => {
            setStateObj(p => ({...p, showWelcomeScreen: false}));
            navigateToUploadScreen();
          }}
          practiceName={apiResponseData?.clinic.name ?? ''}
        />
      </AppointmentPortalPublicPageLayout>
    );
  }

  return (
    <AppointmentPortalPublicPageLayout
      className="payment-login-page payment-dashboard-page appointment-scheduler-dashboard-page"
      testId="pcl-appointmentPortalHomePage">
      <div className="appointment-scheduler-dashboard-wrapper">
      <div style={{flex: 1, display: 'flex'}}>
        {stateObj.isLoading && (
          <div className={classes.loaderWrapper}>
            <Loader />
          </div>
        )}
        {!stateObj.isLoading && (
          <Card
            className={['payments-dashboard-content-wrap', classes.card].join(
              ' '
            )}>
            <CardContent className={[classes.cardPadding, 'content'].join(' ')}>
              <div className="dashboard-content-view">
                <div className={classes.logoutButtonWrapper}>
                  <Button
                    onClick={logoutAction}
                    size={'large'}
                    variant={'outlined'}
                    color="primary">
                    {t('APPOINTMENTS_SCHEDULAR_PORTAL.LOGOUT')}
                  </Button>
                </div>

                {/* clinic name and address view start */}
                <div className={classes.scrollSection}>
                  <Typography
                    className="clinic-address input-header"
                    variant={'h3'}
                    component={'div'}>
                    <div className="titleView">
                      <div
                        className={['clinic-name', classes.displayFlex].join(
                          ' '
                        )}>
                        <p>{apiResponseData?.clinic?.name}</p>
                        {locations &&
                          locations?.length > 0 &&
                          !apiResponseData?.group
                            ?.consolidated_group_billing_enable && (
                            <p
                              className={[
                                classes.link,
                                classes.linkPadding
                              ].join(' ')}
                              onClick={() => {
                                setChangeLocation(true);
                              }}>
                              {t('PAYMENTS_LOGIN.CHANGE_LOCATION.TITLE')}
                            </p>
                          )}
                      </div>
                      <div className="address-text">
                        {HelperUtils.formatFullAddress({
                          line1: apiResponseData?.clinic?.address,
                          line2: apiResponseData?.clinic?.address_line_2 || ''
                        })}
                      </div>
                      <div className="address-text">
                        {HelperUtils.formatFullAddress({
                          city: apiResponseData?.clinic?.city,
                          state: apiResponseData?.clinic?.state + ',',
                          country: '',
                          zip: (apiResponseData?.clinic?.zip || '').toString()
                        })}
                      </div>
                      <div className="address-text">
                        {apiResponseData?.clinic?.phone ===
                        apiResponseData?.clinic?.billing_phone ? (
                          apiResponseData?.clinic?.phone && (
                            <p>
                              <span className={classes.labelWrapper}>
                                {t('PAYMENTS_LOGIN.PRACTICE_PHONE_LABEL')}
                              </span>
                              <PhoneFormatter
                                value={
                                  apiResponseData?.clinic.phone.startsWith('+1')
                                    ? apiResponseData?.clinic?.phone
                                    : '+1' + apiResponseData?.clinic?.phone
                                }
                              />
                            </p>
                          )
                        ) : (
                          <>
                            <p>
                              {apiResponseData?.clinic?.phone && (
                                <>
                                  <span className={classes.labelWrapper}>
                                    {t('PAYMENTS_LOGIN.PRACTICE_PHONE_LABEL')}
                                  </span>
                                  <PhoneFormatter
                                    value={
                                      apiResponseData?.clinic.phone.startsWith(
                                        '+1'
                                      )
                                        ? apiResponseData?.clinic?.phone
                                        : '+1' + apiResponseData?.clinic?.phone
                                    }
                                  />
                                </>
                              )}
                            </p>
                            <p>
                              {apiResponseData?.clinic?.billing_phone && (
                                <>
                                  <span className={classes.labelWrapper}>
                                    {t('PAYMENTS_LOGIN.BILLING_PHONE_LABEL')}
                                  </span>
                                  <PhoneFormatter
                                    value={
                                      apiResponseData?.clinic.billing_phone.startsWith(
                                        '+1'
                                      )
                                        ? apiResponseData?.clinic?.billing_phone
                                        : '+1' +
                                          apiResponseData?.clinic?.billing_phone
                                    }
                                  />
                                </>
                              )}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="logoView">
                      {apiResponseData?.clinic?.clinic_logo_url ? (
                        <img
                          alt={'clinic-logo'}
                          src={apiResponseData?.clinic?.clinic_logo_url}
                        />
                      ) : (
                        <ClinicGroupsIcon
                          style={{height: '64px', width: '64px'}}
                        />
                      )}
                    </div>
                  </Typography>
                  {/* clinic name and address view end */}

                  {/* Guarantor name view start */}
                  <Typography
                    className="patient-name input-header"
                    component={'div'}>
                    {t('PAYMENTS_LOGIN.DASHBOARD.HELLO', {
                      name:
                        HelperUtils.formCapitalizeFullName(
                          loggedInPatient?.first_name ?? '',
                          loggedInPatient?.last_name ?? '',
                          loggedInPatient?.middle_name ?? ''
                        ) ?? ''
                    })}
                    ,
                  </Typography>
                  {/* Guarantor name view end */}

                  {/* payments info text view start */}
                  <Typography
                    className="cards-info-text input-header"
                    component={'div'}>
                    <br />
                    <Trans
                      i18nKey={
                        'APPOINTMENTS_SCHEDULAR_PORTAL.HOME_SCREEN.HELPER_TEXT'
                      }
                      components={[
                        <Link
                          key={'X'}
                          variant="body2"
                          // className={classes.ClickHere}
                          type={'button'}
                          component="button"
                          disabled={isLoadingAppointmentList}
                          onClick={() =>
                            history.push(
                              `/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.BASE}/${token}/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.UPLOAD_INSURANCE}?edit=true`
                            )
                          }
                          className={classes.clickHereLink}
                        />
                      ]}
                    />
                    <br />
                  </Typography>
                  <Typography
                    className={classes.schedules}
                    style={{
                      textAlign: 'start',
                      fontSize: '1.2rem',
                      marginBottom: '1rem'
                    }}>
                    {t('APPOINTMENTS_SCHEDULAR_PORTAL.SCHEDULED_APPOINTMENT')}
                  </Typography>
                  {/* payments info text view end*/}

                  {renderEachVaccineCard()}

                  {/* pay on desktop view start */}
                </div>
                {/* pay on desktop view end */}
              </div>
              <div className={`buttonView`}>
                {/** COmmented the view statements button code for time being */}
                {/* <div
                className={`button-sub-text-paid-amount`}
                style={{
                  border:
                    // viewButtonAlert
                    //   ? `2px solid ${APP_PRIMARY_COLOR}`
                    //   :
                    `1px solid ${BORDER_COLOR}`
                }}
              onClick={viewAllStatements}>*/}

                {/* Render Go to Payments Button */}
                {/* <Button
                  className={`button-sub-text-paid-amount`}
                  size={'medium'}
                  color="primary"
                  style={{
                    border: goToPaymentsButtonEvent
                      ? `2px solid ${APP_PRIMARY_COLOR}`
                      : `1px solid ${BORDER_COLOR}`,
                    textTransform: 'capitalize',
                    fontSize: '1rem'
                  }}
                  onClick={() => {
                    setGoToPaymentsButtonEvent(true);
                    history.push(
                      `/${NavRoutes.PUBLIC.PAYMENTS_DASHBOARD}/${token}`
                    );
                    dispatch(_guarantorLogin(true));
                    if (guarantorPortalIsLogin) {
                      dispatch(_appointmentPortalLogin(false));
                    }
                  }}>
                  {'Go to payments'}
                </Button> */}

                <Button
                  fullWidth
                  variant="contained"
                  size={'medium'}
                  color="primary"
                  className={[classes.loginButton].join(' ')}
                  type={'submit'}
                  // disabled={disablePayButton}
                  onClick={openScheduleChoiceDialog}>
                  {t(
                    'APPOINTMENTS_SCHEDULAR_PORTAL.SCHEDULE_APPOINTMENT_BUTTON_LABEL'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      {changeLocation && (
        <PracticeSelection
          guarantor={{
            firstName: apiResponseData?.guarantor.first_name || '',
            middleName: apiResponseData?.guarantor.middle_name || '',
            lastName: apiResponseData?.guarantor.last_name || ''
          }}
          open={changeLocation}
          title={t('PAYMENTS_LOGIN.CHANGE_LOCATION.DIALOG_TITLE')}
          fullScreen={!isAppointmentPortalDesktop}
          groupDetails={{
            name: apiResponseData?.group.group_name || '',
            address: apiResponseData?.group.address || '',
            address2: apiResponseData?.group.address_line_2 || '',
            zip: apiResponseData?.group.zip || '',
            city: apiResponseData?.group.city || '',
            state: apiResponseData?.group.state || ''
          }}
          practiceList={locations.map(l => {
            return {
              id: l.id,
              address: l.address || '',
              address2: l.address_line_2 || '',
              city: l.city,
              state: l.state,
              zip: l.zip || '',
              name: l.name,
              clinicLogoUrl: l.clinic_logo_url || null,
              balance: l.guarantor_due?.current_due_amount || 0
            };
          })}
          onClose={() => {
            setChangeLocation(false);
          }}
          // TO DO HANDLE PRACTICE SELECTION.
          onClick={clinic => {
            handleSelectPractice(clinic);
            setChangeLocation(false);
          }}
          selectedPractice={{
            zip: apiResponseData?.clinic?.zip || '',
            address: apiResponseData?.clinic?.address || '',
            address2: apiResponseData?.clinic?.address_line_2 || '',
            city: apiResponseData?.clinic?.city || '',
            id: apiResponseData?.clinic?.id || 0,
            name: apiResponseData?.clinic?.name || '',
            state: apiResponseData?.clinic?.state || '',
            clinicLogoUrl: null
          }}
          fromAppointmentPortal={true}
        />
      )}
      <ScheduleAppointmentChoiceDialog
        open={showScheduleChoiceDialog}
        onClose={() => setShowScheduleChoiceDialog(false)}
        onSelectAssistant={navigateToVoiceAssistant}
        onSelectNormal={handleSelectNormalScheduling}
        voiceAssistantShortCode={voiceAssistantShortCode}
      />
      </div>
    </AppointmentPortalPublicPageLayout>
  );
};

export default AppointmentSchedulerHome;
